import { ReactNode } from "react";
import { AdminNavbarGroup } from "@/lib/Components/Common/AdminNavbarGroup";
import {
  QueryTasksWhereColumn,
  SqlOperator,
  TaskStatus,
  TaskType,
  WorkOrderAssignmentStatus,
  WorkOrderStatus,
} from "@/gql/graphql";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { useMatchRoute } from "@tanstack/react-router";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { workOrderStatusCountQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/WorkOrderStatusCountQuery";
import {
  Navbar,
  NavbarItemLink,
  NavbarSection,
} from "@/components/catalyst/navbar";
import { useTranslations } from "use-intl";
import { useThreadUnreadCount } from "@/app/Threads/hooks/useThreadUnreadCount";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { taskListQuery } from "@/app/Tasks/GraphQL/taskListQuery";

type Filter = {
  label: ReactNode;
  statuses: WorkOrderStatus[];
  badgeColor?: BadgeColor;
  hideBadge?: boolean;
  isAdmin?: boolean;
};

function useFilters(): Filter[] {
  const t = useTranslations("workOrder.navigation");

  return [
    {
      label: t("inProgress"),
      statuses: [
        WorkOrderStatus.QuoteRequested,
        WorkOrderStatus.QuoteIssued,
        WorkOrderStatus.AssigningDrivers,
        WorkOrderStatus.DriversAssigned,
        WorkOrderStatus.InProgress,
        WorkOrderStatus.Invoiced,
      ],
      badgeColor: "yellow",
    },
    {
      label: t("completed"),
      statuses: [WorkOrderStatus.Completed],
      hideBadge: true,
    },
    {
      label: t("cancelled"),
      statuses: [WorkOrderStatus.QuoteRejected, WorkOrderStatus.Archived],
      hideBadge: true,
    },
    {
      label: t("all"),
      statuses: [],
    },
  ];
}

export function ProDriverLayoutHeader() {
  "use no memo";

  const t = useTranslations("proDriver");
  const matchRoute = useMatchRoute();
  const { isAdmin } = useTenant();
  const { data } = useGqlQuery(workOrderStatusCountQuery, {});
  const { data: taskList } = useGqlQuery(
    taskListQuery,
    {
      page: 1,
      first: 10,
      status: [TaskStatus.Pending, TaskStatus.Overdue],
      where: {
        AND: [
          {
            column: QueryTasksWhereColumn.Type,
            operator: SqlOperator.In,
            value: [TaskType.ReviewDriverReceipts],
          },
        ],
      },
    },
    { enabled: isAdmin },
  );

  const filters = useFilters();
  const { supplierId } = useGlobalSupplier();
  const unreadThreadCount = useThreadUnreadCount(supplierId);
  const taskCount = taskList?.tasks.paginatorInfo.count ?? 0;

  return (
    <div className="relative border-gray-200 sm:border-b sm:pb-0">
      <h3 className="text-base font-semibold leading-6 text-gray-900">
        {t("title")}
      </h3>
      <Navbar className="-mb-px">
        <NavbarSection className="max-lg:hidden">
          {filters.map(({ label, statuses, hideBadge, badgeColor }, idx) => {
            const count = statuses.reduce((acc, status) => {
              return (
                acc +
                (data?.workOrderStatusCount.find(
                  (counts) => counts.status === status,
                )?.count ?? 0)
              );
            }, 0);

            return (
              <NavbarItemLink
                key={idx}
                to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders"
                params={(prev) => prev}
                current={
                  !!matchRoute({
                    to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders",
                    search: {
                      workOrderStatus: statuses,
                    },
                  })
                }
                search={{
                  workOrderStatus: statuses,
                }}
              >
                {label}

                {count && !hideBadge ? (
                  <Badge className="py-0" color={badgeColor}>
                    {count}
                  </Badge>
                ) : null}
              </NavbarItemLink>
            );
          })}

          <NavbarItemLink
            to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/messages"
            params={(prev) => prev}
            current={
              !!matchRoute({
                to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/messages",
                fuzzy: true,
              })
            }
          >
            <ChatBubbleLeftRightIcon
              aria-hidden="true"
              className="size-4"
              data-slot="icon"
            />
            <span>Messages</span>
            {unreadThreadCount > 0 ? (
              <span className="ml-2 inline shrink-0 rounded-md bg-indigo-500 px-2 text-white">
                {unreadThreadCount > 99 ? "99+" : unreadThreadCount}
              </span>
            ) : null}
          </NavbarItemLink>

          <NavbarItemLink
            to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents"
            params={(prev) => prev}
            current={
              !!matchRoute({
                to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents",
                fuzzy: true,
              })
            }
          >
            {t("incidents")}
          </NavbarItemLink>

          {isAdmin ? (
            <AdminNavbarGroup>
              <NavbarItemLink
                to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/assignments"
                params={(prev) => prev}
                current={
                  !!matchRoute({
                    to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/assignments",
                    fuzzy: true,
                  })
                }
                search={{
                  assignmentStatus: [
                    WorkOrderAssignmentStatus.Assigned,
                    WorkOrderAssignmentStatus.OnRoad,
                    WorkOrderAssignmentStatus.Delivered,
                    WorkOrderAssignmentStatus.ReceiptsUploaded,
                  ],
                }}
              >
                {t("assignments")}
              </NavbarItemLink>

              <NavbarItemLink
                to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/tasks"
                params={(prev) => prev}
                current={
                  !!matchRoute({
                    to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/tasks",
                    fuzzy: true,
                  })
                }
              >
                {t("tasks")}
                {taskCount > 0 ? (
                  <span className="ml-2 inline shrink-0 rounded-md bg-indigo-500 px-2 text-white">
                    {taskCount}
                  </span>
                ) : null}
              </NavbarItemLink>

              <NavbarItemLink
                to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/drivers"
                params={(prev) => prev}
                current={
                  !!matchRoute({
                    to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/drivers",
                  })
                }
              >
                {t("drivers")}
              </NavbarItemLink>
            </AdminNavbarGroup>
          ) : null}
        </NavbarSection>
      </Navbar>
    </div>
  );
}
